import * as THREE from "three"

export const latLngToVector = (lat: number, lng: number, radius: number) => {
  const phi = (90 - lat) * (Math.PI / 180)
  const theta = (lng + 180) * (Math.PI / 180)
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  )
}
export const latLngToUnitVector = (lat: number, lng: number): THREE.Vector3 => latLngToVector(lat, lng, 1)
export const vectorToLatLng = (v: THREE.Vector3): { lat: number; lng: number } => ({
  lat: Math.asin(Math.max(-1, Math.min(1, v.y))) * (180 / Math.PI),
  lng: Math.atan2(-v.z, v.x) * (180 / Math.PI)
})

const pointInPolygon = (lng: number, lat: number, ring: number[][]): boolean => {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersect = ((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

const normalizeLng = (lng: number): number => {
  while (lng < -180) lng += 360
  while (lng > 180) lng -= 360
  return lng
}

type LatLng = { lng: number, lat: number }

const alignLngToReference = (lng: number, reference: number): number => {
  let aligned = lng
  while (aligned - reference > 180) aligned -= 360
  while (aligned - reference < -180) aligned += 360
  return aligned
}

const unwrapRing = (ring: number[][]): LatLng[] => {
  if (ring.length === 0) return []
  const result: LatLng[] = []
  let prevRawLng = normalizeLng(ring[0][0])
  let offset = 0
  result.push({ lng: prevRawLng, lat: ring[0][1] })
  for (let i = 1; i < ring.length; i++) {
    const currentRaw = normalizeLng(ring[i][0])
    const diff = currentRaw - prevRawLng
    if (diff > 180) offset -= 360
    if (diff < -180) offset += 360
    const unwrappedLng = currentRaw + offset
    result.push({ lng: unwrappedLng, lat: ring[i][1] })
    prevRawLng = currentRaw
  }
  return result
}

const closeRing = (points: LatLng[]): LatLng[] => {
  if (points.length === 0) return points
  const last = points[points.length - 1]
  const first = points[0]
  const alignedFirstLng = alignLngToReference(first.lng, last.lng)
  return [...points, { lng: alignedFirstLng, lat: first.lat }]
}

const findIntersectionsWithLng = (points: LatLng[], targetLng: number): [number, number][] => {
  const intersections: [number, number][] = []
  if (points.length < 2) return intersections
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    if (p1.lng === p2.lng) continue
    const reference = (p1.lng + p2.lng) / 2
    const alignedTarget = alignLngToReference(targetLng, reference)
    const minLng = Math.min(p1.lng, p2.lng) - 1e-9
    const maxLng = Math.max(p1.lng, p2.lng) + 1e-9
    if (alignedTarget < minLng || alignedTarget > maxLng) continue
    const t = (alignedTarget - p1.lng) / (p2.lng - p1.lng)
    if (t < 0 || t > 1) continue
    const lat = p1.lat + (p2.lat - p1.lat) * t
    intersections.push([normalizeLng(alignedTarget), lat])
  }
  return intersections
}

const findIntersectionsWithLat = (points: LatLng[], targetLat: number): [number, number][] => {
  const intersections: [number, number][] = []
  if (points.length < 2) return intersections
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    if (p1.lat === p2.lat) continue
    const minLat = Math.min(p1.lat, p2.lat) - 1e-9
    const maxLat = Math.max(p1.lat, p2.lat) + 1e-9
    if (targetLat < minLat || targetLat > maxLat) continue
    const t = (targetLat - p1.lat) / (p2.lat - p1.lat)
    if (t < 0 || t > 1) continue
    const lng = p1.lng + (p2.lng - p1.lng) * t
    intersections.push([normalizeLng(lng), targetLat])
  }
  return intersections
}

const angularDistance = (a: number, b: number): number => {
  let diff = Math.abs(a - b)
  if (diff > 180) diff = 360 - diff
  return diff
}

const getClosestPointOnRing = (points: LatLng[], targetLng: number, targetLat: number): [number, number] => {
  if (points.length === 0) return [targetLng, targetLat]
  let closest: [number, number] = [normalizeLng(points[0].lng), points[0].lat]
  let minDist = distanceSq(closest[0], closest[1], targetLng, targetLat)
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    const dx = p2.lng - p1.lng
    const dy = p2.lat - p1.lat
    const segLenSq = dx * dx + dy * dy
    if (segLenSq === 0) continue
    const reference = (p1.lng + p2.lng) / 2
    const alignedTargetLng = alignLngToReference(targetLng, reference)
    const tNumerator = (alignedTargetLng - p1.lng) * dx + (targetLat - p1.lat) * dy
    const t = Math.max(0, Math.min(1, tNumerator / segLenSq))
    const projLng = p1.lng + dx * t
    const projLat = p1.lat + dy * t
    const dist = distanceSq(normalizeLng(projLng), projLat, targetLng, targetLat)
    if (dist < minDist) {
      minDist = dist
      closest = [normalizeLng(projLng), projLat]
    }
  }
  return closest
}

const averageLng = (a: number, b: number): number => {
  let diff = b - a
  if (diff > 180) diff -= 360
  if (diff < -180) diff += 360
  return normalizeLng(a + diff / 2)
}

const distanceSq = (lng1: number, lat1: number, lng2: number, lat2: number): number => {
  let dlng = Math.abs(lng1 - lng2)
  if (dlng > 180) dlng = 360 - dlng
  const dlat = lat1 - lat2
  return dlng * dlng + dlat * dlat
}

export const createCountryGeometry = (coordinates: number[][][], radius: number, altitude: number = 0.01) => {
  const positions: number[] = []
  const indices: number[] = []
  const normals: number[] = []
  
  coordinates.forEach((ring) => {
    if (ring.length < 3) return
    
    const lngs = ring.map(([lng]) => lng)
    const lats = ring.map(([, lat]) => lat)
    
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    
    const lngSpan = maxLng - minLng
    const crossesAntimeridian = lngSpan > 180
    
    let gridMinLng = minLng
    let gridMaxLng = maxLng
    
    if (crossesAntimeridian) {
      const sortedLngs = [...lngs].sort((a, b) => a - b)
      let maxGap = 0
      let gapStart = 0
      for (let i = 0; i < sortedLngs.length - 1; i++) {
        const gap = sortedLngs[i + 1] - sortedLngs[i]
        if (gap > maxGap) {
          maxGap = gap
          gapStart = i
        }
      }
      if (maxGap > 180) {
        gridMinLng = sortedLngs[gapStart + 1]
        gridMaxLng = sortedLngs[gapStart] + 360
      }
    }
    
    const gridSize = 20
    const lngStep = (gridMaxLng - gridMinLng) / gridSize
    const latStep = (maxLat - minLat) / gridSize
    
    const closedRing = closeRing(unwrapRing(ring))
    const gridVertices: Map<string, number> = new Map()
    const borderVertices: Map<string, number> = new Map()
    const getPositionVector = (idx: number): THREE.Vector3 => {
      const base = idx * 3
      return new THREE.Vector3(positions[base], positions[base + 1], positions[base + 2])
    }
    const pushBoundaryTriangle = (a: number, b: number, c: number) => {
      const va = getPositionVector(a)
      const vb = getPositionVector(b)
      const vc = getPositionVector(c)
      const normal = vb.clone().sub(va).cross(vc.clone().sub(va))
      const centroid = va.clone().add(vb).add(vc)
      if (normal.dot(centroid) < 0) {
        indices.push(a, c, b)
      } else {
        indices.push(a, b, c)
      }
    }
    const getVertexIndex = (lng: number, lat: number): number => {
      const normalizedLng = normalizeLng(lng)
      const key = `${normalizedLng.toFixed(6)},${lat.toFixed(6)}`
      if (!gridVertices.has(key)) {
        const v = latLngToVector(lat, normalizedLng, radius + altitude)
        const idx = positions.length / 3
        positions.push(v.x, v.y, v.z)
        const normal = v.clone().normalize()
        normals.push(normal.x, normal.y, normal.z)
        gridVertices.set(key, idx)
      }
      return gridVertices.get(key)!
    }
    const getOrCreateCustomVertex = (lng: number, lat: number): number => {
      const normalizedLng = normalizeLng(lng)
      const key = `${normalizedLng.toFixed(6)},${lat.toFixed(6)}`
      if (gridVertices.has(key)) return gridVertices.get(key)!
      if (borderVertices.has(key)) return borderVertices.get(key)!
      const v = latLngToVector(lat, normalizedLng, radius + altitude)
      const idx = positions.length / 3
      positions.push(v.x, v.y, v.z)
      const normal = v.clone().normalize()
      normals.push(normal.x, normal.y, normal.z)
      borderVertices.set(key, idx)
      return idx
    }
    const pointInPolygonWithAntimeridian = (lng: number, lat: number, ring: number[][]): boolean => {
      if (!crossesAntimeridian) {
        return pointInPolygon(lng, lat, ring)
      }
      const normalizedLng = normalizeLng(lng)
      const normalizedRing = ring.map(([lng, lat]) => [normalizeLng(lng), lat])
      return pointInPolygon(normalizedLng, lat, normalizedRing)
    }
    
    const cellData: Array<Array<null | {
      idx00: number,
      idx10: number,
      idx01: number,
      idx11: number,
      lng1: number,
      lng2: number,
      lat1: number,
      lat2: number
    }>> = Array.from({ length: gridSize }, () => Array(gridSize).fill(null))
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        let lng1 = gridMinLng + i * lngStep
        const lat1 = minLat + j * latStep
        let lng2 = gridMinLng + (i + 1) * lngStep
        const lat2 = minLat + (j + 1) * latStep
        
        if (crossesAntimeridian) {
          lng1 = normalizeLng(lng1)
          lng2 = normalizeLng(lng2)
        }
        
        const centerLng = normalizeLng((lng1 + lng2) / 2)
        const centerLat = (lat1 + lat2) / 2
        
        if (pointInPolygonWithAntimeridian(centerLng, centerLat, ring)) {
          const idx00 = getVertexIndex(lng1, lat1)
          const idx10 = getVertexIndex(lng2, lat1)
          const idx01 = getVertexIndex(lng1, lat2)
          const idx11 = getVertexIndex(lng2, lat2)
          cellData[i][j] = { idx00, idx10, idx01, idx11, lng1, lng2, lat1, lat2 }
          
          indices.push(idx00, idx10, idx01)
          indices.push(idx10, idx11, idx01)
        }
      }
    }
    
    const boundaryEdges: { idxA: number, idxB: number, centerLng: number, centerLat: number, orientation: 'vertical' | 'horizontal' }[] = []
    const addBoundaryEdge = (idxA: number, idxB: number, centerLng: number, centerLat: number, orientation: 'vertical' | 'horizontal') => {
      boundaryEdges.push({ idxA, idxB, centerLng, centerLat, orientation })
    }
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const cell = cellData[i][j]
        if (!cell) continue
        if (i === 0 || !cellData[i - 1] || !cellData[i - 1][j]) {
          addBoundaryEdge(cell.idx00, cell.idx01, cell.lng1, (cell.lat1 + cell.lat2) / 2, 'vertical')
        }
        if (i === gridSize - 1 || !cellData[i + 1] || !cellData[i + 1][j]) {
          addBoundaryEdge(cell.idx10, cell.idx11, cell.lng2, (cell.lat1 + cell.lat2) / 2, 'vertical')
        }
        if (j === 0 || !cellData[i][j - 1]) {
          addBoundaryEdge(cell.idx00, cell.idx10, averageLng(cell.lng1, cell.lng2), cell.lat1, 'horizontal')
        }
        if (j === gridSize - 1 || !cellData[i][j + 1]) {
          addBoundaryEdge(cell.idx01, cell.idx11, averageLng(cell.lng1, cell.lng2), cell.lat2, 'horizontal')
        }
      }
    }
    
    boundaryEdges.forEach((edge) => {
      let coordinate: [number, number] | null = null
      if (edge.orientation === 'vertical') {
        const intersections = findIntersectionsWithLng(closedRing, edge.centerLng)
        if (intersections.length > 0) {
          coordinate = intersections.reduce((best, candidate) => {
            const bestDiff = Math.abs(best[1] - edge.centerLat)
            const candidateDiff = Math.abs(candidate[1] - edge.centerLat)
            return candidateDiff < bestDiff ? candidate : best
          })
        }
      } else {
        const intersections = findIntersectionsWithLat(closedRing, edge.centerLat)
        if (intersections.length > 0) {
          coordinate = intersections.reduce((best, candidate) => {
            const bestDiff = angularDistance(best[0], edge.centerLng)
            const candidateDiff = angularDistance(candidate[0], edge.centerLng)
            return candidateDiff < bestDiff ? candidate : best
          })
        }
      }
      if (!coordinate) {
        coordinate = getClosestPointOnRing(closedRing, edge.centerLng, edge.centerLat)
      }
      const [lng, lat] = coordinate
      const customIdx = getOrCreateCustomVertex(lng, lat)
      pushBoundaryTriangle(edge.idxA, customIdx, edge.idxB)
    })
  })
  
  if (positions.length === 0) return null
  
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  geometry.setIndex(indices)
  
  return geometry
}

export const createCountryBorderGeometry = (coordinates: number[][][], radius: number, altitude: number = 0.01) => {
  const positions: number[] = []
  
  coordinates.forEach((ring) => {
    if (ring.length < 2) return
    
    ring.forEach(([lng, lat]) => {
      const v = latLngToVector(lat, lng, radius + altitude)
      positions.push(v.x, v.y, v.z)
    })
  })
  
  if (positions.length === 0) return null
  
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  
  return geometry
}
